import { useEffect, useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { User, Mail, Phone, Shield, Lock } from 'lucide-react'
import LoadingSpinner from '../components/LoadingSpinner'
import toast from 'react-hot-toast'
import PageHeader from '../components/PageHeader'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { businessProfileService } from '@/services/businessProfileService'

export default function ProfilePage() {
  const { currentUser, userProfile, updateUserProfile, changePassword } = useAuth()
  const [loading, setLoading] = useState(false)
  const [passwordLoading, setPasswordLoading] = useState(false)
  const [businessLoading, setBusinessLoading] = useState(false)

  const [profileData, setProfileData] = useState({
    name: userProfile?.name || '',
    phone: userProfile?.phone || '',
    email: currentUser?.email || '',
  })

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  })

  const canEditBusiness = userProfile?.role === 'owner' || userProfile?.role === 'manager'
  const [businessData, setBusinessData] = useState({
    tradingName: '',
    legalName: '',
    registrationNumber: '',
    vatNumber: '',
    address: '',
    phone: '',
    email: '',
    receiptFooter: '',
  })

  useEffect(() => {
    let cancelled = false

    async function loadBusinessProfile() {
      if (!canEditBusiness) return
      try {
        setBusinessLoading(true)
        const profile = await businessProfileService.get()
        if (cancelled) return
        setBusinessData({
          tradingName: profile.tradingName || '',
          legalName: profile.legalName || '',
          registrationNumber: profile.registrationNumber || '',
          vatNumber: profile.vatNumber || '',
          address: profile.address || '',
          phone: profile.phone || '',
          email: profile.email || '',
          receiptFooter: profile.receiptFooter || '',
        })
      } catch (error) {
        console.error('Error loading business profile:', error)
        toast.error('Failed to load business details')
      } finally {
        if (!cancelled) setBusinessLoading(false)
      }
    }

    loadBusinessProfile()
    return () => {
      cancelled = true
    }
  }, [canEditBusiness])

  function handleProfileChange(e) {
    setProfileData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }))
  }

  function handlePasswordChange(e) {
    setPasswordData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }))
  }

  function handleBusinessChange(e) {
    setBusinessData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }))
  }

  async function handleProfileSubmit(e) {
    e.preventDefault()

    if (!profileData.name || !profileData.phone) {
      toast.error('Please fill in all fields')
      return
    }

    setLoading(true)
    try {
      await updateUserProfile({
        name: profileData.name,
        phone: profileData.phone,
        displayName: profileData.name,
      })
    } catch (error) {
      console.error('Error updating profile:', error)
    } finally {
      setLoading(false)
    }
  }

  async function handlePasswordSubmit(e) {
    e.preventDefault()

    const { currentPassword, newPassword, confirmPassword } = passwordData

    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error('Please fill in all password fields')
      return
    }

    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match')
      return
    }

    if (newPassword.length < 6) {
      toast.error('New password must be at least 6 characters')
      return
    }

    setPasswordLoading(true)
    try {
      await changePassword(currentPassword, newPassword)
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      })
    } catch (error) {
      console.error('Error changing password:', error)
    } finally {
      setPasswordLoading(false)
    }
  }

  async function handleBusinessSubmit(e) {
    e.preventDefault()

    if (!canEditBusiness) return

    if (!businessData.tradingName) {
      toast.error('Trading name is required')
      return
    }

    setBusinessLoading(true)
    try {
      await businessProfileService.update(businessData)
      toast.success('Business details updated')
    } catch (error) {
      console.error('Error updating business details:', error)
      toast.error('Failed to update business details')
    } finally {
      setBusinessLoading(false)
    }
  }

  return (
    <div className="mx-auto max-w-5xl space-y-8">
      <PageHeader
        eyebrow="Account"
        title="Profile settings"
        description="Your details and password for the business console."
      />

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <Card className="shadow-sm border-border/80">
            <CardHeader>
              <div className="flex items-center gap-2">
                <div className="rounded-lg bg-primary/10 p-2 text-primary">
                  <User className="size-5" />
                </div>
                <div>
                  <CardTitle className="text-lg">Profile</CardTitle>
                  <CardDescription>How you appear to your team</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleProfileSubmit} className="space-y-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="name">Full name</Label>
                    <Input
                      id="name"
                      name="name"
                      required
                      value={profileData.name}
                      onChange={handleProfileChange}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      disabled
                      className="bg-muted/50"
                      value={profileData.email}
                    />
                    <p className="text-xs text-muted-foreground">Email is tied to login and cannot be changed here.</p>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    required
                    value={profileData.phone}
                    onChange={handleProfileChange}
                  />
                </div>
                <div className="flex justify-end pt-2">
                  <Button type="submit" disabled={loading}>
                    {loading ? (
                      <>
                        <LoadingSpinner size="small" className="mr-2" />
                        Saving…
                      </>
                    ) : (
                      'Save changes'
                    )}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>

          <Card className="shadow-sm border-border/80">
            <CardHeader>
              <div className="flex items-center gap-2">
                <div className="rounded-lg bg-muted p-2 text-muted-foreground">
                  <Lock className="size-5" />
                </div>
                <div>
                  <CardTitle className="text-lg">Password</CardTitle>
                  <CardDescription>Update your sign-in password</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={handlePasswordSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="currentPassword">Current password</Label>
                  <Input
                    id="currentPassword"
                    name="currentPassword"
                    type="password"
                    required
                    value={passwordData.currentPassword}
                    onChange={handlePasswordChange}
                  />
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="newPassword">New password</Label>
                    <Input
                      id="newPassword"
                      name="newPassword"
                      type="password"
                      required
                      value={passwordData.newPassword}
                      onChange={handlePasswordChange}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword">Confirm new password</Label>
                    <Input
                      id="confirmPassword"
                      name="confirmPassword"
                      type="password"
                      required
                      value={passwordData.confirmPassword}
                      onChange={handlePasswordChange}
                    />
                  </div>
                </div>
                <div className="flex justify-end pt-2">
                  <Button type="submit" disabled={passwordLoading}>
                    {passwordLoading ? (
                      <>
                        <LoadingSpinner size="small" className="mr-2" />
                        Updating…
                      </>
                    ) : (
                      'Change password'
                    )}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>

          {canEditBusiness && (
            <Card className="shadow-sm border-border/80">
              <CardHeader>
                <CardTitle className="text-lg">Business details</CardTitle>
                <CardDescription>
                  Appears on receipts/invoices for tax and contact purposes.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleBusinessSubmit} className="space-y-4">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="tradingName">Trading name</Label>
                      <Input
                        id="tradingName"
                        name="tradingName"
                        required
                        value={businessData.tradingName}
                        onChange={handleBusinessChange}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="legalName">Legal name (optional)</Label>
                      <Input
                        id="legalName"
                        name="legalName"
                        value={businessData.legalName}
                        onChange={handleBusinessChange}
                      />
                    </div>
                  </div>

                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="registrationNumber">Registration number</Label>
                      <Input
                        id="registrationNumber"
                        name="registrationNumber"
                        value={businessData.registrationNumber}
                        onChange={handleBusinessChange}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="vatNumber">VAT number</Label>
                      <Input
                        id="vatNumber"
                        name="vatNumber"
                        value={businessData.vatNumber}
                        onChange={handleBusinessChange}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="address">Address</Label>
                    <Input
                      id="address"
                      name="address"
                      value={businessData.address}
                      onChange={handleBusinessChange}
                    />
                  </div>

                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="businessPhone">Phone</Label>
                      <Input
                        id="businessPhone"
                        name="phone"
                        value={businessData.phone}
                        onChange={handleBusinessChange}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="businessEmail">Email</Label>
                      <Input
                        id="businessEmail"
                        name="email"
                        type="email"
                        value={businessData.email}
                        onChange={handleBusinessChange}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="receiptFooter">Receipt footer (optional)</Label>
                    <Input
                      id="receiptFooter"
                      name="receiptFooter"
                      value={businessData.receiptFooter}
                      onChange={handleBusinessChange}
                      placeholder="e.g. Thanks for your support"
                    />
                  </div>

                  <div className="flex justify-end pt-2">
                    <Button type="submit" disabled={businessLoading}>
                      {businessLoading ? (
                        <>
                          <LoadingSpinner size="small" className="mr-2" />
                          Saving…
                        </>
                      ) : (
                        'Save business details'
                      )}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          )}
        </div>

        <div className="space-y-6">
          <Card className="shadow-sm border-border/80">
            <CardHeader>
              <CardTitle className="text-base">Summary</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-3">
                <Mail className="size-4 shrink-0 text-muted-foreground mt-0.5" />
                <div>
                  <p className="text-xs font-medium text-muted-foreground">Email</p>
                  <p className="text-sm text-foreground break-all">{currentUser?.email}</p>
                </div>
              </div>
              <Separator />
              <div className="flex gap-3">
                <Phone className="size-4 shrink-0 text-muted-foreground mt-0.5" />
                <div>
                  <p className="text-xs font-medium text-muted-foreground">Phone</p>
                  <p className="text-sm text-foreground">{userProfile?.phone || 'N/A'}</p>
                </div>
              </div>
              <Separator />
              <div className="flex gap-3">
                <Shield className="size-4 shrink-0 text-muted-foreground mt-0.5" />
                <div>
                  <p className="text-xs font-medium text-muted-foreground">Role</p>
                  <p className="text-sm capitalize text-foreground">{userProfile?.role || 'N/A'}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-sm border-border/80 bg-primary/5 border-primary/15">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-medium text-foreground">Account status</span>
                <Badge variant="default" className="bg-emerald-600 hover:bg-emerald-600">
                  Active
                </Badge>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}
